// Omnix/client/ui/ConversationList.js
import { ComponentBase } from "../core/ComponentBase.js";
import { createAvatarElement } from "../../helpers/avatarHelper.js";

export class ConversationList extends ComponentBase {
    static styles = `
        .conversation-list-wrapper {
            display: flex;
            flex-direction: column;
            height: 100%;
            overflow: hidden;
            background-color: var(--bs-secondary-bg); 
        }
        .conversation-list-header {
            padding: 0.75rem 1rem;
            border-bottom: 1px solid var(--bs-border-color);
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-shrink: 0;
            background-color: var(--bs-secondary-bg);
        }
        .header-title {
            font-size: 1.1rem;
            font-weight: 500;
            color: var(--bs-emphasis-color);
        }
        .search-bar-wrapper {
            padding: 0.5rem 1rem;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            flex-shrink: 0;
        }
        .conversations-scroll-area {
            flex-grow: 1;
            overflow-y: auto;
        }
        .no-conversations {
            text-align: center;
            padding: 2rem;
            color: var(--bs-secondary-color);
        }
        .conversation-filter-bar {
            padding: 0.5rem 1rem;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-shrink: 0;
        }
        .conversation-filter-bar .clear-filters-btn {
            margin-left: auto;
        }

        /* Styles from old ConversationListItem */
        .conversation-list-item {
            display: flex;
            align-items: center;
            padding: 0.75rem 1rem;
            cursor: pointer;
            border-bottom: 1px solid var(--bs-border-color-translucent);
            transition: background-color 0.15s ease-in-out;
            position: relative;
        }
        .conversation-list-item:hover { background-color: var(--bs-tertiary-bg); }
        .conversation-list-item.active {
            background-color: var(--bs-primary-bg-subtle);
            border-left: 3px solid var(--bs-primary);
            padding-left: calc(1rem - 3px);
        }
        .conversation-list-item.is-closed {
            opacity: 0.65; /* De-emphasize the entire item */
        }
        .conversation-list-item.is-closed .conv-name {
            text-decoration: line-through; /* Add a subtle strikethrough to the name for clarity */
        }

        .conv-details { flex-grow: 1; overflow: hidden; }
        .conv-name-time { display: flex; justify-content: space-between; align-items: baseline; }
        .conv-name { font-weight: 500; color: var(--bs-emphasis-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .conv-last-message-time { font-size: 0.75rem; color: var(--bs-secondary-color); white-space: nowrap; margin-left: 0.5rem; }
        .conv-last-message-snippet { font-size: 0.85rem; color: var(--bs-secondary-color); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; }
        .conv-channel-icon { margin-right: 0.4rem; }
        .conv-unread-badge { background-color: var(--bs-primary); color: white; border-radius: 50%; font-size: 0.7rem; padding: 0.1rem 0.4rem; margin-left: auto; min-width: 18px; text-align: center; }
        .conv-last-message-snippet.typing-indicator { font-style: italic; color: var(--bs-primary); }
        
    `;
    // --- END OF FIX (Part 1) ---

    constructor(config) {
        super(config);
        this.conversations = [];
        this.filteredConversations = [];
        this.activeConversationId = null;
        this._debouncedSearch = this.debounce(this.filterConversations.bind(this), 250);
        this.typingStatusMap = new Map();
        this.TYPING_CLEAR_TIMEOUT_MS = 5000;
        this.activeFilters = { channel: null, status: null, assigneeId: null, tag: null };
        this.filterComponents = {};
        this.assignableAgents = [];
        this.applyStyles();
    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'conversation-list-wrapper';
        this.container.innerHTML = `
            <div class="conversation-list-header">
                <h5 class="header-title mb-0" data-i18n="Inbox">Inbox</h5>
                <div class="header-actions btn-group btn-group-sm gap-2">
                    <button class="btn btn-outline-secondary" id="refresh-conv-btn" title="Refresh"><i class="bi bi-arrow-clockwise"></i></button>
                    <button class="btn btn-sm btn-primary" id="new-conv-btn" title="New Conversation"><i class="bi bi-plus-lg"></i></button>
                </div>
            </div>
            <div class="search-bar-wrapper">
                <input type="search" class="form-control form-control-sm" placeholder="Search conversations...">
            </div>
            <div class="conversation-filter-bar"></div> 
            <div class="conversations-scroll-area"></div>        `;

        this.headerElement = this.container.querySelector('.conversation-list-header');
        this.searchInput = this.container.querySelector('.search-bar-wrapper');
        this.filterBar = this.container.querySelector('.conversation-filter-bar');
        this.scrollArea = this.container.querySelector('.conversations-scroll-area');

        // --- START OF FIX (Part 2: Event Delegation) ---
        // Add ONE event listener to the parent container. This is the key to fixing the bug.
        this.scrollArea.addEventListener('click', (event) => {
            const itemElement = event.target.closest('.conversation-list-item');
            if (itemElement && itemElement.dataset.id) {
                const conversationId = itemElement.dataset.id;
                const conversationObject = this.conversations.find(c => c._id === conversationId);
                if (conversationObject) {
                    // Emit the full object, which is what the parent (UnifiedInbox) expects.
                    this.emit('conversationSelected', conversationObject);
                }
            }
        });

        this.container.querySelector('#new-conv-btn').addEventListener('click', () => this.emit('newConversationClicked'));
        this.container.querySelector('#refresh-conv-btn').addEventListener('click', () => this.loadConversations());

        this.searchInput.addEventListener('input', (e) => this._debouncedSearch(e.target.value));

        await this.initializeFilters();
        await super.initialize();
    }

    // This method ONLY manipulates CSS classes. It does NOT re-render the list.
    setActiveConversation(conversationId) {
        if (this.activeConversationId === conversationId) return;

        // Remove 'active' class from the previously selected item, if any
        if (this.activeConversationId) {
            const oldActiveElement = this.scrollArea.querySelector(`[data-id="${this.activeConversationId}"]`);
            if (oldActiveElement) {
                oldActiveElement.classList.remove('active');
            }
        }

        // Add 'active' class to the newly selected item
        if (conversationId) {
            const newActiveElement = this.scrollArea.querySelector(`[data-id="${conversationId}"]`);
            if (newActiveElement) {
                newActiveElement.classList.add('active');
            }
        }

        // Update the internal state
        this.activeConversationId = conversationId;
    }

    _createConversationItemElement(convData) {
        const item = document.createElement('div');
        item.className = 'conversation-list-item';
        item.dataset.id = convData._id; // Use MongoDB _id as the unique identifier

        if (convData._id === this.activeConversationId) {
            item.classList.add('active');
        }

        if (convData.status === 'closed') {
            item.classList.add('is-closed');
        }

        const contact = convData.contact;
        const displayName = contact ? (contact.firstName || contact.email || 'Unknown') : this.t('Deleted Contact');
        const avatarUrl = contact ? contact.avatarUrl : null;

        const avatarElement = createAvatarElement({
            displayName: displayName,
            avatarUrl: avatarUrl,
            sizeClass: 'default',
            additionalClasses: ['me-2']
        });

        const lastMessageTime = convData.lastInteractionTimestamp
            ? new Date(convData.lastInteractionTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            : '';

        const channelIcons = {
            email: { icon: 'bi-envelope-fill', color: 'text-danger' },
            sms: { icon: 'bi-chat-dots-fill', color: 'text-success' },
            whatsapp: { icon: 'bi-whatsapp', color: 'text-success' },
            web_chat: { icon: 'bi-globe', color: 'text-primary' },
            facebook_messenger: { icon: 'bi-messenger', color: 'text-primary' },
            instagram: { icon: 'bi-instagram', color: 'text-warning' },
            telegram: { icon: 'bi-telegram', color: 'text-info' },
            voice: { icon: 'bi-telephone-fill', color: 'text-secondary' },
            form: { icon: 'bi-file-earmark-text-fill', color: 'text-secondary' }
        };
        const channelInfo = channelIcons[convData.channelType] || { icon: 'bi-question-circle', color: 'text-muted' };
        const channelIconHtml = `<i class="bi ${channelInfo.icon} ${channelInfo.color} conv-channel-icon"></i>`;

        const unreadCount = convData.unreadCount || 0;
        const unreadBadgeHtml = unreadCount > 0 ? `<span class="conv-unread-badge">${unreadCount > 9 ? '9+' : unreadCount}</span>` : '';

        const typingUsersInConv = this.typingStatusMap.get(convData._id);
        const typingDisplayNames = typingUsersInConv ? Array.from(typingUsersInConv.values()).map(u => u.displayName) : [];

        let snippetHtml;
        if (typingDisplayNames.length > 0) {
            let typingText = this.t("{{name}} is typing...", { name: typingDisplayNames[0] });
            snippetHtml = `<span class="typing-indicator">${typingText}</span>`;
        } else {
            let snippetText = convData.lastInteractionSnippet || '';
            if (convData.channelType === 'voice') {
                if (snippetText.startsWith('Call Ended')) {
                    const durationMatch = snippetText.match(/(\d+)s/);
                    if (durationMatch) {
                        const seconds = parseInt(durationMatch[1], 10);
                        const minutes = Math.floor(seconds / 60);
                        const remainingSeconds = seconds % 60;
                        snippetText = `Call Ended (${minutes}m ${remainingSeconds}s)`;
                    }
                }
            }
            snippetHtml = `<span>${snippetText}</span>${unreadBadgeHtml}`;
        }

        item.innerHTML = `
            ${avatarElement.outerHTML}
            <div class="conv-details">
                <div class="conv-name-time">
                    <span class="conv-name">${displayName}</span>
                    <span class="conv-last-message-time">${lastMessageTime}</span>
                </div>
                <div class="conv-last-message-snippet">
                    ${channelIconHtml}
                    ${snippetHtml}
                </div>
            </div>
        `;
        return item;
    }


    renderConversationItems() {
        if (!this.scrollArea) return;
        this.scrollArea.innerHTML = ''; // Clear the container

        if (this.filteredConversations.length === 0) {
            this.scrollArea.innerHTML = `<div class="no-conversations">${this.t('No conversations found.')}</div>`;
            return;
        }

        this.filteredConversations.sort((a, b) =>
            new Date(b.lastInteractionTimestamp || 0) - new Date(a.lastInteractionTimestamp || 0)
        );

        const fragment = document.createDocumentFragment();
        this.filteredConversations.forEach(convData => {
            const itemElement = this._createConversationItemElement(convData);
            fragment.appendChild(itemElement);
        });

        this.scrollArea.appendChild(fragment);
    }

    async initAfterLogin() {
        await this.fetchAssignableAgents(); // Fetch data for filters
        await this.loadConversations();
    }

    addNewConversation(newConvData) {
        const existingIndex = this.conversations.findIndex(c => c._id === newConvData._id);

        if (existingIndex === -1) {
            this.conversations.unshift(newConvData);

            this.filterConversations(this.searchInput.querySelector('input')?.value || '');

            this.log.info(`Added new conversation to list via addNewConversation: ${newConvData._id}`);
        } else {
            this.conversations[existingIndex] = { ...this.conversations[existingIndex], ...newConvData };
            this.filterConversations(this.searchInput.querySelector('input')?.value || '');
            this.log.info(`addNewConversation called for existing conversation ${newConvData._id}. Updating it.`);
        }
    }


    async fetchAssignableAgents() {
        try {
            this.assignableAgents = await this.appClient.rpcClient.call('UnifiedInboxRpc.getAssignableAgents', {});
            // After fetching, re-render the assignee filter dropdown with the new options
            if (this.filterComponents.assignee) {
                this.filterComponents.assignee.updateOptions(this.assignableAgents, this.activeFilters.assigneeId);
            }
        } catch (error) {
            this.log.error("Failed to fetch assignable agents:", error);
            this.assignableAgents = [];
        }
    }

    async initializeFilters() {
        const channelOptions = [
            { value: 'email', label: 'Email', icon: 'bi-envelope-fill' },
            { value: 'voice', label: 'Voice', icon: 'bi-telephone-fill' },
            { value: 'sms', label: 'SMS', icon: 'bi-chat-dots-fill' },
            { value: 'whatsapp', label: 'WhatsApp', icon: 'bi-whatsapp' },
            { value: 'web_chat', label: 'Web Chat', icon: 'bi-globe' },
            { value: 'facebook_messenger', label: 'Messenger', icon: 'bi-messenger' },
            { value: 'instagram', label: 'Instagram', icon: 'bi-instagram' },
            { value: 'telegram', label: 'Telegram', icon: 'bi-telegram' },
        ];
        const statusOptions = [
            { value: 'open', label: 'Open', icon: 'bi-folder2-open' },
            { value: 'pending', label: 'Pending', icon: 'bi-clock-history' },
            { value: 'closed', label: 'Closed', icon: 'bi-check-circle-fill' },
        ];
        const tagOptions = [ // Example tags
            { value: 'urgent', label: 'Urgent', icon: 'bi-flag-fill' },
            { value: 'sales-lead', label: 'Sales Lead', icon: 'bi-currency-dollar' },
            { value: 'support-ticket', label: 'Support Ticket', icon: 'bi-headset' },
        ];

        const filterConfigs = {
            channel: { icon: 'bi-hdd-stack-fill', title: 'Channel', options: channelOptions },
            status: { icon: 'bi-toggles', title: 'Status', options: statusOptions },
            assignee: { icon: 'bi-person-check-fill', title: 'Assignee', options: this.assignableAgents },
            tag: { icon: 'bi-tags-fill', title: 'Tag', options: tagOptions },
        };

        for (const [key, config] of Object.entries(filterConfigs)) {
            const filterComponent = await this.createManagedComponent('FilterDropdown', this.filterBar, {
                instanceId: `${this.config.instanceId}-filter-${key}`,
                title: config.title,
                icon: config.icon,
                options: config.options,
                activeValue: this.activeFilters[key]
            });
            filterComponent.on('change', ({ value }) => this.handleFilterChange(key, value));
            this.filterComponents[key] = filterComponent;
        }

        // Add Clear Filters button
        this.clearFiltersBtn = document.createElement('button');
        this.clearFiltersBtn.className = 'btn btn-sm btn-outline-secondary clear-filters-btn';
        this.clearFiltersBtn.innerHTML = `<i class="bi bi-x-lg"></i> ${this.t('Clear')}`;
        this.clearFiltersBtn.addEventListener('click', () => this.clearAllFilters());
        this.filterBar.appendChild(this.clearFiltersBtn);
        this.updateClearFiltersButtonVisibility();
    }

    handleFilterChange(filterType, value) {
        this.activeFilters[filterType] = value;
        this.log.info(`Filter changed:`, this.activeFilters);
        this.loadConversations(); // Reload data from server with new filters
        this.updateClearFiltersButtonVisibility();
        // Also update the specific filter dropdown to show its active state
        if (this.filterComponents[filterType]) {
            this.filterComponents[filterType].updateOptions(this.filterComponents[filterType].options, value);
        }
    }

    filterConversations(searchTerm = "") {
        const lowerTerm = searchTerm.toLowerCase();
        if (!lowerTerm) {
            this.filteredConversations = [...this.conversations];
        } else {
            this.filteredConversations = this.conversations.filter(c =>
                `${c.contact?.firstName || ''} ${c.contact?.lastName || ''}`.toLowerCase().includes(lowerTerm) ||
                (c.contact?.email || '').toLowerCase().includes(lowerTerm) ||
                (c.lastInteractionSnippet || '').toLowerCase().includes(lowerTerm)
            );
        }
        this.renderConversationItems();
    }


    render() {
        this.appClient.tx.applyTranslations(this.container);
        return this.container;
    }

    clearAllFilters() {
        this.activeFilters = { channel: null, status: null, assigneeId: null, tag: null };
        this.log.info(`All filters cleared.`);
        this.loadConversations();
        this.updateClearFiltersButtonVisibility();
        // Update all filter dropdowns to show inactive state
        for (const key in this.filterComponents) {
            this.filterComponents[key].updateOptions(this.filterComponents[key].options, null);
        }
    }

    updateClearFiltersButtonVisibility() {
        const hasActiveFilter = Object.values(this.activeFilters).some(v => v !== null && v !== undefined && v !== '');
        if (this.clearFiltersBtn) {
            this.clearFiltersBtn.style.display = hasActiveFilter ? 'inline-flex' : 'none';
        }
    }

    async loadConversations() {
        try {
            if (this.scrollArea) {
                this.scrollArea.innerHTML = `<div class="p-4 text-center text-muted">${this.t('Loading conversations...')}</div>`;
            }

            const cleanFilter = Object.entries(this.activeFilters).reduce((acc, [key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    acc[key] = value;
                }
                return acc;
            }, {});

            const result = await this.appClient.rpcClient.call('UnifiedInboxRpc.getConversations', {
                filter: cleanFilter
            });

            this.conversations = result.data || [];
            this.filterConversations(this.searchInput.querySelector('input')?.value || '');
        } catch (error) {
            this.log.error("Failed to load conversations:", error);
            if (this.scrollArea) {
                this.scrollArea.innerHTML = `<p class="no-conversations text-danger">${this.t('Failed to load chats.')}</p>`;
                this.appClient.tx.applyTranslations(this.scrollArea);
            }
        }
    }


    handleTypingStatusUpdate({ conversationId, user }) {
        const { userId, displayName, isTyping } = user;
        if (!this.typingStatusMap.has(conversationId)) {
            this.typingStatusMap.set(conversationId, new Map());
        }
        const conversationTypers = this.typingStatusMap.get(conversationId);

        if (isTyping) {
            if (conversationTypers.has(userId)) {
                clearTimeout(conversationTypers.get(userId).timeoutId);
            }
            const timeoutId = setTimeout(() => {
                conversationTypers.delete(userId);
                this.updateSpecificListItem(conversationId);
            }, this.TYPING_CLEAR_TIMEOUT_MS);
            conversationTypers.set(userId, { displayName, timeoutId });
        } else {
            if (conversationTypers.has(userId)) {
                clearTimeout(conversationTypers.get(userId).timeoutId);
                conversationTypers.delete(userId);
            }
        }
        this.updateSpecificListItem(conversationId);
    }

    _updateListItem(conversationId) {
        const itemElement = this.scrollArea.querySelector(`[data-id="${conversationId}"]`);
        const convData = this.conversations.find(c => c._id === conversationId);

        if (itemElement && convData) {
            // Create a new, updated element
            const newItemElement = this._createConversationItemElement(convData);
            // Replace the old one in the DOM
            itemElement.replaceWith(newItemElement);
        }
    }

    handleRealtimeEvent(eventData) {
        const eventType = eventData.type;

        if (eventType === 'typingStatus') {
            this.handleTypingStatusUpdate(eventData);
            return;
        }

        if (eventType === 'newInteraction') {
            // A new message changes the order, so a full re-render/re-sort is necessary.
            // This logic is correct and should remain.
            const { conversation: eventConversation, interaction } = eventData;
            const conversationId = eventConversation._id;
            const conversationInList = this.conversations.find(c => c._id === conversationId);

            if (conversationInList) {
                conversationInList.lastInteractionSnippet = eventConversation.lastInteractionSnippet;
                conversationInList.lastInteractionTimestamp = eventConversation.lastInteractionTimestamp;
                if (conversationId !== this.activeConversationId && interaction.senderId !== this.myContactId) {
                    conversationInList.unreadCount = (conversationInList.unreadCount || 0) + 1;
                }
                this.conversations = this.conversations.filter(c => c._id !== conversationId);
                this.conversations.unshift(conversationInList);
            } else {
                const newConversation = eventConversation;
                if (conversationId !== this.activeConversationId && interaction.senderId !== this.myContactId) {
                    newConversation.unreadCount = 1;
                }
                this.conversations.unshift(newConversation);
            }
            this.filterConversations(this.searchInput.querySelector('input')?.value || '');

        } else if (eventType === 'conversationUpdated') {
            // A status change does NOT affect order, so we can do a targeted update.
            const { conversationId, updates } = eventData;
            const conv = this.conversations.find(c => c._id === conversationId);
            if (conv) {
                Object.assign(conv, updates);
                // Instead of re-rendering everything, just update the specific item.
                this._updateListItem(conversationId);
            }
        }
    }

    resetUnreadCount(conversationId) {
        const conversation = this.conversations.find(c => c._id === conversationId);
        if (conversation && conversation.unreadCount > 0) {
            conversation.unreadCount = 0;
            // Also use the targeted update method here.
            this._updateListItem(conversationId);
        }
    }


    updateSpecificListItem(conversationId) {
        const itemElement = this.scrollArea.querySelector(`[data-id="${conversationId}"]`);
        const convData = this.conversations.find(c => c._id === conversationId);
        if (itemElement && convData) {
            const newItemElement = this._createConversationItemElement(convData);
            itemElement.replaceWith(newItemElement);
        }
    }


    debounce(func, delay) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), delay);
        };
    }

    destroy() {
        this.listItems.forEach(item => item.destroy());
        this.listItems.clear();
        super.destroy();
    }
}